// src/contexts/AuthContext.js - Fixed Auto-Login Issue
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  sendEmailVerification,
  onAuthStateChanged 
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { sendCustomVerificationEmail, generateVerificationToken } from '../services/emailService';
import { v4 as uuidv4 } from 'uuid';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load user profile from Firestore
  const loadUserProfile = async (user) => {
    if (!user) {
      setUserProfile(null);
      return;
    }

    try {
      console.log('🔍 Loading user profile for:', user.email);
      
      const userDocRef = doc(db, 'users', user.email);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const profile = userDoc.data();
        console.log('✅ User profile loaded:', profile);
        setUserProfile(profile);
      } else {
        console.log('⚠ No user profile found for:', user.email);
        setUserProfile(null);
      }
    } catch (error) {
      console.error('❌ Error loading user profile:', error);
      setUserProfile(null);
    }
  };

  // Create user profile in Firestore
  const createUserProfile = async (user, additionalData = {}) => {
    if (!user) return;

    try {
      console.log('🆕 Creating user profile for:', user.email);
      
      // Generate organizationId from email domain
      const generateOrganizationId = (email) => {
        if (!email) return 'default_org';
        const domain = email.split('@')[1];
        if (!domain) return 'default_org';
        return `org_${domain.replace(/\./g, '_')}`;
      };

      const userDocRef = doc(db, 'users', user.email);
      const profileData = {
        email: user.email,
        emailVerified: user.emailVerified,
        organizationId: generateOrganizationId(user.email),
        createdAt: new Date(),
        lastLogin: new Date(),
        ...additionalData
      };

      await setDoc(userDocRef, profileData, { merge: true });
      console.log('✅ User profile created:', profileData);
      
      setUserProfile(profileData);
      return { success: true, profile: profileData };
    } catch (error) {
      console.error('❌ Error creating user profile:', error);
      return { success: false, error: error.message };
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log('🔄 Auth state changed:', currentUser?.email || 'logged out');
      setUser(currentUser);
      
      if (currentUser) {
        await loadUserProfile(currentUser);
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const result = await signInWithEmailAndPassword(auth, email, password);
      
      // Create profile if it doesn't exist
      const userDocRef = doc(db, 'users', result.user.email);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        console.log('🆕 No profile found, creating one...');
        await createUserProfile(result.user);
      }
      
      return { success: true, user: result.user };
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = 'Er is een fout opgetreden bij het inloggen.';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'Geen account gevonden met dit email adres.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Onjuist wachtwoord.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Ongeldig email adres.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Te veel inlogpogingen. Probeer het later opnieuw.';
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // FIXED: Enhanced register without auto-login when using custom verification
  const register = async (email, password, name, options = {}) => {
    try {
      setError(null);
      console.log('🚀 Starting registration...', { email, options });
      
      const result = await createUserWithEmailAndPassword(auth, email, password);
      console.log('✅ Firebase user created:', result.user.email);

      // Create user profile immediately
      await createUserProfile(result.user, { 
        name: name,
        role: 'user'
      });

      // CRITICAL FIX: Don't auto-login for custom verification
      if (options.skipEmailVerification) {
        console.log('🔐 Custom verification mode - logging out user immediately');
        
        // Sign out the user immediately to prevent auto-login
        await signOut(auth);
        
        // Reset auth state
        setUser(null);
        setUserProfile(null);
        
        return { 
          success: true, 
          user: result.user, // Return user data but don't keep them logged in
          message: 'Account aangemaakt! Check je email voor verificatie.',
          needsVerification: true
        };
      } else {
        // Use Firebase email verification (fallback)
        try {
          await sendEmailVerification(result.user);
          console.log('📧 Firebase verification email sent');
          return { 
            success: true, 
            user: result.user,
            message: 'Account aangemaakt! Check je email voor verificatie.'
          };
        } catch (emailError) {
          console.error('📧 Firebase email verification failed:', emailError);
          return { 
            success: true, 
            user: result.user,
            message: 'Account aangemaakt! (Verificatie email kon niet worden verstuurd - probeer later opnieuw)'
          };
        }
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      let errorMessage = 'Er is een fout opgetreden bij het registreren.';
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'Er bestaat al een account met dit email adres.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Wachtwoord moet minimaal 6 karakters zijn.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Ongeldig email adres.';
      }
      
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Custom verification email sender
  const sendCustomVerification = async (email, name) => {
    try {
      console.log('📧 Sending custom verification email via Brevo...');
      
      const verificationToken = generateVerificationToken();
      const result = await sendCustomVerificationEmail(email, name, verificationToken);
      
      if (result.success) {
        return { 
          success: true, 
          message: 'Verificatie email verzonden via ons professionele email systeem!',
          verificationUrl: result.verificationUrl
        };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Custom verification error:', error);
      return { 
        success: false, 
        error: error.message || 'Kon custom verificatie email niet versturen'
      };
    }
  };

  // Resend verification email (Firebase fallback)
  const resendVerification = async () => {
    if (!user) {
      return { success: false, error: 'Geen gebruiker ingelogd' };
    }

    try {
      await sendEmailVerification(user);
      return { success: true, message: 'Verificatie email opnieuw verstuurd!' };
    } catch (error) {
      console.error('Resend verification error:', error);
      return { success: false, error: 'Kon verificatie email niet versturen' };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  // Custom password reset via Brevo
const resetPassword = async (email) => {
  try {
    setError(null);
    console.log('🔐 Starting custom password reset for:', email);

    // Step 1: Generate unique reset token
    const resetToken = uuidv4();
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 1); // Token expires in 1 hour

    console.log('🎫 Generated reset token:', resetToken);

    // Step 2: Store token in Firestore
    const resetDocRef = doc(db, 'password_resets', resetToken);
    await setDoc(resetDocRef, {
      email: email,
      token: resetToken,
      createdAt: new Date(),
      expiresAt: expiresAt,
      used: false
    });
    console.log('✅ Reset token stored in Firestore');

    // Step 3: Generate reset URL
    const resetUrl = `${window.location.origin}/reset-password?token=${resetToken}&email=${encodeURIComponent(email)}`;
    console.log('🔗 Reset URL:', resetUrl);

    // Step 4: Send email via Firebase Function (Brevo)
    const functionUrl = 'https://us-central1-piramide-scan.cloudfunctions.net/sendBatchEmails';
    
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        emailType: 'password_reset',
        invites: [{
          email: email,
          resetUrl: resetUrl
        }]
      })
    });

    const result = await response.json();
    
    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to send reset email');
    }

    console.log('✅ Password reset email sent successfully');
    return { success: true };

  } catch (error) {
    console.error('❌ Password reset error:', error);
    let errorMessage = 'Er is een fout opgetreden bij het versturen van de reset link.';
    
    if (error.message.includes('user-not-found')) {
      errorMessage = 'Geen account gevonden met dit email adres.';
    } else if (error.message.includes('invalid-email')) {
      errorMessage = 'Ongeldig email adres.';
    }
    
    setError(errorMessage);
    return { success: false, error: errorMessage };
  }
};

  const value = {
    currentUser: user,
    user,
    userProfile,
    loading,
    error,
    setError,
    login,
    register,
    logout,
    resetPassword,
    resendVerification,
    sendCustomVerification,
    createUserProfile,
    loadUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};